import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, Button, Chip, Tab, Tabs, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import PageTopBar from "../../../components/common/PageTopBar";
import { toolbarButtonSx } from "../../../components/common/toolbarStyles";
import { useNotify } from "../../../context/NotificationContext";
import { getFacultyList } from "../../faculty/service/facultyService";
import { ApplyLeaveDialog } from "../components/ApplyLeaveDialog";
import { DecisionDialog } from "../components/DecisionDialog";
import { LeaveTable } from "../components/LeaveTable";
import { decideLeave, fetchFacultyLeave, leaveErrorMessage } from "../services/leaveService";
import { LeaveRequest } from "../types";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "all", label: "All" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

/**
 * Staff → Leave Requests (admin): every faculty request in the active academic year. Pending ones are
 * approved or rejected here; leave can also be added for a faculty (approved at once). The tab is
 * kept in the URL (?tab=).
 */
export default function LeaveRequestsPage() {
  const notify = useNotify();
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab");
  const tab: TabKey = TABS.some((t) => t.key === requested) ? (requested as TabKey) : "pending";

  const [rows, setRows] = useState<LeaveRequest[]>([]);
  const [faculties, setFaculties] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deciding, setDeciding] = useState<{ leave: LeaveRequest; decision: "approve" | "reject" } | null>(null);

  useEffect(() => {
    Promise.all([fetchFacultyLeave(), getFacultyList()])
      .then(([leave, list]) => {
        setRows(leave);
        setFaculties(list.map((f) => ({ id: f.id, name: f.name })).sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => setError("Failed to load leave requests."))
      .finally(() => setLoading(false));
  }, []);

  const pendingCount = rows.filter((r) => r.status === "PENDING").length;
  const visible = useMemo(() => {
    if (tab === "pending") return [...rows.filter((r) => r.status === "PENDING")].sort((a, b) => a.fromDate.localeCompare(b.fromDate));
    if (tab === "approved") return rows.filter((r) => r.status === "APPROVED");
    return rows;
  }, [rows, tab]);

  const replace = (leave: LeaveRequest) => setRows((prev) => prev.map((r) => (r.id === leave.id ? leave : r)));

  const confirmDecision = async (note: string): Promise<string | null> => {
    if (!deciding) return null;
    try {
      replace(await decideLeave(deciding.leave.id, deciding.decision, note));
      notify(deciding.decision === "approve" ? "Leave approved." : "Leave rejected.", "success");
      setDeciding(null);
      return null;
    } catch (err) {
      return leaveErrorMessage(err, "Failed to save the decision.");
    }
  };

  const tabs = (
    <Tabs value={tab} onChange={(_, next: TabKey) => setParams(next === "pending" ? {} : { tab: next }, { replace: true })} sx={{ minHeight: 40 }}>
      {TABS.map((t) => (
        <Tab
          key={t.key}
          value={t.key}
          sx={{ minHeight: 40, textTransform: "none", fontWeight: 600 }}
          label={
            t.key === "pending" && pendingCount > 0 ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                Pending
                <Chip label={pendingCount} size="small" color="warning" sx={{ height: 20, fontSize: 12, fontWeight: 700 }} />
              </Box>
            ) : (
              t.label
            )
          }
        />
      ))}
    </Tabs>
  );

  const emptyText = tab === "pending" ? "No requests waiting for a decision" : tab === "approved" ? "No approved leave yet" : "No leave requests yet";

  return (
    <Box sx={{ mt: 2, mr: 2 }}>
      <PageTopBar
        left={tabs}
        right={
          <Button variant="contained" size="small" sx={toolbarButtonSx} startIcon={<AddIcon />} onClick={() => setAddOpen(true)} disabled={loading}>
            Add Leave
          </Button>
        }
      />

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <LeaveTable
          rows={visible}
          loading={loading}
          showApplicant
          empty={
            <Box>
              <InboxOutlinedIcon sx={{ fontSize: 40, color: "grey.400", mb: 1 }} />
              <Typography fontWeight={600}>{emptyText}</Typography>
              <Typography variant="body2" color="text.secondary">
                Faculty apply from My Space → My Leave.
              </Typography>
            </Box>
          }
          actions={(leave) =>
            leave.status === "PENDING" && (
              <>
                <Button
                  size="small"
                  color="success"
                  variant="outlined"
                  startIcon={<CheckIcon />}
                  onClick={() => setDeciding({ leave, decision: "approve" })}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Approve
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => setDeciding({ leave, decision: "reject" })}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Reject
                </Button>
              </>
            )
          }
        />
      )}

      <ApplyLeaveDialog
        open={addOpen}
        mode="admin"
        faculties={faculties}
        existing={rows}
        onClose={() => setAddOpen(false)}
        onApplied={(leave) => {
          setRows((prev) => [leave, ...prev]);
          setAddOpen(false);
          notify(`Leave added for ${leave.applicantName}.`, "success");
        }}
      />

      <DecisionDialog leave={deciding?.leave ?? null} decision={deciding?.decision ?? "approve"} onClose={() => setDeciding(null)} onConfirm={confirmDecision} />
    </Box>
  );
}
